import { Money } from '@betterment/core';
import { timeFormat } from 'd3-time-format';

export const formatDate = timeFormat('%b %d, %Y');

const DEFAULT_RESOLUTION = 5 * 60 * 1000;

type EmptyChartDatumForDate = {
  dateTime: Date;
};

export const emptyChartDatumForDate = ({ dateTime }: EmptyChartDatumForDate) => {
  return {
    __typename: 'FinancialSecurityPricePerformancePricePoint' as const,
    dateTime,
    amount: Money(0),
    returns: {
      __typename: 'FinancialSecurityPriceReturns' as const,
      amount: Money(0),
      percentage: 0,
    },
  } as PricePointType;
};

type EmptyChartDataForDate = {
  date: Date;
};

export const emptyChartDataForDate = ({ date }: EmptyChartDataForDate): PricePointType[] => {
  return [emptyChartDatumForDate({ dateTime: date })];
};

type TimeRangeBucketsType = {
  startTime: Date;
  endTime: Date;
  resolution?: number;
};

export const timeRangeBuckets = ({ startTime, endTime, resolution = DEFAULT_RESOLUTION }: TimeRangeBucketsType) => {
  const buckets = [];

  for (let timestamp = startTime.getTime(); timestamp < endTime.getTime(); timestamp += resolution) {
    const intervalStart = new Date(timestamp);
    const nextTimestamp = timestamp + resolution;
    const isLastItem = nextTimestamp >= endTime.getTime();
    const intervalEnd = isLastItem ? endTime : new Date(nextTimestamp);

    buckets.push({
      startAt: intervalStart,
      endAt: intervalEnd,
      isLast: isLastItem,
    });
  }

  return buckets;
};

type ReturnsType = {
  __typename: string;
  amount: Money | null;
  percentage: number | null;
};

export type PricePointType = {
  __typename: string;
  dateTime: Date;
  amount: Money | null;
  returns: ReturnsType;
};

type IntradayPricePointsForTimeRangeType = {
  startTime: Date;
  endTime: Date;
  pricePoints: PricePointType[];
};

export const intradayPricePointsForTimeRange = ({
  startTime,
  endTime,
  pricePoints,
}: IntradayPricePointsForTimeRangeType): PricePointType[] => {
  const buckets = timeRangeBuckets({
    startTime,
    endTime,
  });

  const firstAmountCents = pricePoints[0]?.amount?.cents ?? null;

  return buckets.map(bucket => {
    const filteredPricePoints = pricePoints.filter(pricePoint => {
      const pointTime = pricePoint.dateTime.getTime();
      return pointTime >= bucket.startAt.getTime() && pointTime < bucket.endAt.getTime();
    });

    const amount =
      filteredPricePoints.length > 0
        ? filteredPricePoints.reduce((sum, point) => sum + (point.amount?.cents ?? 0), 0) / filteredPricePoints.length
        : null;

    const dateTime = bucket.isLast ? bucket.endAt : bucket.startAt;

    let returnAmountCents = null;
    let returnPercent = null;

    if (amount !== null && firstAmountCents !== null) {
      returnAmountCents = amount - firstAmountCents;
      returnPercent = firstAmountCents > 0 ? (returnAmountCents / firstAmountCents) * 100 : null;
    }

    const returnPercentage = returnPercent !== null ? Math.round(returnPercent * 100) / 100 : null;

    return {
      __typename: 'FinancialSecurityPricePerformancePricePoint' as const,
      dateTime,
      amount: amount !== null ? Money(amount) : null,
      returns: {
        __typename: 'FinancialSecurityPriceReturns' as const,
        amount: returnAmountCents !== null ? Money(returnAmountCents) : null,
        percentage: returnPercentage,
      },
    };
  });
};

export const formatDateTime = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const part = (type: string) => parts.find(p => p.type === type)?.value;
  const day = `${part('month')} ${part('day')}, ${part('year')}`;
  const time = `${part('hour')}:${part('minute')}${part('dayPeriod')?.toLowerCase()} ET`;
  return `${day} at ${time}`;
};

export const formatTimeDisplay = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const part = (type: string) => parts.find(p => p.type === type)?.value;
  const timeDisplay = `${part('hour')}:${part('minute')}${part('dayPeriod')?.toLowerCase()} ET`;

  return timeDisplay;
};
